<template>
  <b-modal
    id="superadmin-tenant-switcher"
    v-model="showModal"
    title="Tenant Switcher"
    size="lg"
    centered
    scrollable
  >
    <template #modal-header>
      <div class="d-flex align-items-center">
        <feather-icon icon="BriefcaseIcon" class="mr-1" variant="primary" />
        <h4 class="modal-title text-primary mb-0">
          Switch Tenant
        </h4>
      </div>
    </template>

    <!-- Search -->
    <b-form-group label="Search Tenants" label-class="font-small-3 mb-25">
      <b-input-group size="sm">
        <b-form-input
          v-model="searchQuery"
          placeholder="Search by name, code, or facility type..."
          @input="filterTenants"
        />
        <b-input-group-append>
          <b-button variant="outline-secondary" disabled>
            <feather-icon icon="SearchIcon" size="12" />
          </b-button>
        </b-input-group-append>
      </b-input-group>
    </b-form-group>

    <!-- Current Tenant -->
    <b-alert variant="info" show class="mb-1 py-50 px-1 current-tenant-alert">
      <div class="d-flex align-items-center">
        <feather-icon icon="MapPinIcon" size="14" class="mr-50 flex-shrink-0" />
        <div class="d-flex flex-column" style="line-height: 1.2; min-width: 0">
          <span class="font-small-3"><strong>Current:</strong> {{ currentTenantName }}</span>
          <small class="text-muted" style="font-size: 0.65rem">
            {{ currentTenantId === 0 ? 'All tenants access' : `ID: ${currentTenantId}` }}
          </small>
        </div>
      </div>
    </b-alert>

    <!-- Quick Access -->
    <div class="d-flex flex-wrap mb-1" style="gap: 0.35rem">
      <b-button
        variant="outline-primary"
        size="sm"
        class="quick-pill"
        @click="switchToAllTenants"
      >
        <feather-icon icon="GlobeIcon" size="12" class="mr-25" />
        All Tenants
      </b-button>
      <b-button
        v-for="tenant in recentTenants"
        :key="`recent-${tenant.id}`"
        variant="outline-secondary"
        size="sm"
        class="quick-pill"
        @click="selectTenant(tenant)"
      >
        {{ tenant.name }}
      </b-button>
    </div>

    <!-- Tenant List (compact, scrollable) -->
    <div class="tenant-list-wrapper">
      <b-list-group flush>
        <b-list-group-item
          v-for="tenant in filteredTenants"
          :key="tenant.id"
          :variant="tenant.id === currentTenantId ? 'primary' : ''"
          :class="['tenant-row py-50 px-1', { 'list-group-item-action': tenant.id !== currentTenantId }]"
          @click="tenant.id !== currentTenantId && selectTenant(tenant)"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex flex-column" style="min-width: 0; line-height: 1.2">
              <span class="font-weight-bold tenant-name text-truncate">
                {{ tenant.name }}
                <b-badge
                  v-if="tenant.id === currentTenantId"
                  variant="light"
                  class="ml-50 align-middle"
                  style="font-size: 0.6rem"
                >
                  Current
                </b-badge>
              </span>
              <small class="text-muted tenant-meta text-truncate">
                {{ tenant.code }} · {{ tenant.jenis_faskes }} · ID {{ tenant.id }}
              </small>
            </div>
            <feather-icon
              v-if="tenant.id === currentTenantId"
              icon="CheckCircleIcon"
              size="14"
              class="text-primary flex-shrink-0 ml-1"
            />
            <feather-icon
              v-else
              icon="ArrowRightIcon"
              size="14"
              class="text-muted flex-shrink-0 ml-1"
            />
          </div>
        </b-list-group-item>
      </b-list-group>

      <!-- No results -->
      <div v-if="filteredTenants.length === 0" class="text-center py-2">
        <feather-icon icon="SearchIcon" size="32" class="text-muted mb-50" />
        <p class="text-muted mb-0 font-small-3">No tenants found matching your search.</p>
      </div>
    </div>

    <!-- Stats -->
    <div class="mt-50 text-center">
      <small class="text-muted" style="font-size: 0.65rem">
        Showing {{ filteredTenants.length }} of {{ availableTenants.length }} tenants
      </small>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="showModal = false">
        Cancel
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'

export default {
  name: 'SuperadminTenantSwitcher',
  
  data() {
    return {
      showModal: false,
      searchQuery: '',
      filteredTenants: [],
      recentTenants: []
    }
  },
  
  computed: {
    ...mapGetters('superadmin', ['currentTenantName', 'currentTenantId']),
    ...mapState('superadmin', ['availableTenants'])
  },
  
  watch: {
    availableTenants: {
      immediate: true,
      handler() {
        this.filterTenants()
        this.loadRecentTenants()
      }
    }
  },
  
  methods: {
    ...mapActions('superadmin', ['switchTenant']),
    
    show() {
      this.showModal = true
      this.searchQuery = ''
      this.filterTenants()
      this.loadRecentTenants()
    },
    
    filterTenants() {
      if (!this.searchQuery.trim()) {
        this.filteredTenants = [...this.availableTenants]
      } else {
        const query = this.searchQuery.toLowerCase()
        this.filteredTenants = this.availableTenants.filter(tenant => 
          tenant.name.toLowerCase().includes(query) ||
          tenant.code.toLowerCase().includes(query) ||
          tenant.jenis_faskes?.toLowerCase().includes(query) ||
          tenant.id.toString().includes(query)
        )
      }
      
      // Sort by name
      this.filteredTenants.sort((a, b) => a.name.localeCompare(b.name))
    },
    
    loadRecentTenants() {
      // Preserve recency order from localStorage; cap at 5
      const recent = JSON.parse(localStorage.getItem('superadmin_recent_tenants') || '[]')
      this.recentTenants = recent
        .map(id => this.availableTenants.find(t => t.id === id))
        .filter(Boolean)
        .slice(0, 5)
    },
    
    saveRecentTenant(tenantId) {
      let recent = JSON.parse(localStorage.getItem('superadmin_recent_tenants') || '[]')
      recent = recent.filter(id => id !== tenantId) // Remove if exists
      recent.unshift(tenantId) // Add to beginning
      recent = recent.slice(0, 5) // Keep only latest 5
      localStorage.setItem('superadmin_recent_tenants', JSON.stringify(recent))
      this.loadRecentTenants()
    },
    
    async selectTenant(tenant) {
      try {
        await this.switchTenant(tenant.id)
        this.saveRecentTenant(tenant.id)
        this.showModal = false

        this.$dekatoast.success(`Tenant switched - Now viewing: ${tenant.name}`)

        // Navigate to dashboard to reflect new tenant context
        if (this.$route.name !== 'dashboard') {
          this.$router.push({ name: 'dashboard' })
        } else {
          this.$router.replace({ name: 'dashboard', query: { _t: Date.now() } })
        }
      } catch (error) {
        this.$dekatoast.error(error.response?.data?.message || 'Failed to switch tenant')
      }
    },

    async switchToAllTenants() {
      try {
        await this.switchTenant(0)
        this.showModal = false

        this.$dekatoast.info('Viewing All Tenants - Access to all tenant data enabled')

        // Navigate to dashboard to reflect new tenant context
        if (this.$route.name !== 'dashboard') {
          this.$router.push({ name: 'dashboard' })
        } else {
          this.$router.replace({ name: 'dashboard', query: { _t: Date.now() } })
        }
      } catch (error) {
        this.$dekatoast.error('Failed to switch to all tenants mode')
      }
    }
  }
}
</script>

<style scoped>
.list-group-item-action {
  cursor: pointer;
}

.list-group-item-action:hover {
  background-color: #f5f7fa;
}

.list-group-item.list-group-item-primary {
  cursor: default;
}

.current-tenant-alert {
  border-radius: 0.358rem;
}

.quick-pill {
  font-size: 0.7rem;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
}

/* Compact, scrollable tenant list */
.tenant-list-wrapper {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #ebe9f1;
  border-radius: 0.358rem;
}

.tenant-list-wrapper::-webkit-scrollbar {
  width: 6px;
}

.tenant-list-wrapper::-webkit-scrollbar-thumb {
  background: #d8d6de;
  border-radius: 3px;
}

.tenant-list-wrapper::-webkit-scrollbar-thumb:hover {
  background: #b8b6c0;
}

.tenant-row {
  border-left: 0;
  border-right: 0;
  border-radius: 0 !important;
}

.tenant-row:first-child {
  border-top: 0;
}

.tenant-row:last-child {
  border-bottom: 0;
}

.tenant-name {
  font-size: 0.8rem;
  max-width: 100%;
  display: inline-block;
  vertical-align: middle;
}

.tenant-meta {
  font-size: 0.68rem;
}
</style>